/* eslint-disable no-shadow */
import React, { useState, useEffect } from 'react';
import { connect } from 'react-redux';
import { Modal } from 'react-bootstrap';
import { bindActionCreators } from 'redux';
import i18n from 'i18next';
import { ToastComponent } from '@syncfusion/ej2-react-notifications';

import {
  addOrUpdateSign,
  getUserAccount,
  deleteSign
} from '../../../services/accounts';
import { setFirmas } from '../../../actions/lexon';
import UserSign from '../menu-user-sign';
import { Confirmation } from '../../notification/confirmation';
import { getStringSizeInKB } from '../../../utils/utils';
import TemplateLogo from '../../../assets/icons/firma/firma-logo.svg?react';

const AccountSign = ({
  showUserSignPopover,
  setShowUserSignPopover,
  lexon,
  setFirmas
}) => {
  const { userId, account, lexonToken } = lexon;
  const [signs, setSigns] = useState([]);
  const [editing, setEditing] = useState(false);
  const [currentSign, setCurrentSign] = useState('');
  const [isDefault, setIsDefault] = useState(false);
  const [, setIsLoading] = useState(false);
  const [currentAccount, setCurrentAccount] = useState({});
  const [editSignName, setEditSignName] = useState('');
  const [editingAccount, setEditingAccount] = useState(null);
  const [deleteConfirmationOpen, setDeleteConfirmationOpen] = useState(false);
  const [deleteSelectedSign, setDeleteSelectedSign] = useState(null);
  const [showSizeErrorModal, setShowSizeErrorModal] = useState(false);
  const [toastObj, setToastObj] = useState(null);

  useEffect(() => () => {
    setShowUserSignPopover(false);
    return () => {
      if (toastObj) {
        toastObj.hide('All');
      }
    };
  }, []);

  const getAccount = async () => {
    setIsLoading(true);
    const response = await getUserAccount(userId, account, lexonToken);
    setCurrentAccount(response.data);
    setIsLoading(false);
  };

  useEffect(() => {
    (async () => {
      getAccount();
    })();
  }, [showUserSignPopover, editing]);

  useEffect(() => {
    setSigns(currentAccount?.firmas || []);
    setFirmas(currentAccount?.firmas || []);
  }, [currentAccount]);

  const addNewSign = () => {
    setEditing(true);
    setCurrentSign('');
    setEditSignName('');
    setIsDefault(false);
  };

  const handleRemoveSign = async (sign, removeSignEditing = false) => {
    try {
      setIsLoading(true);
      await deleteSign({
        userId,
        provider: currentAccount.provider,
        email: currentAccount.email,
        name: sign.name,
        lexonToken
      });
      await getAccount();
      setIsLoading(false);
      if (!removeSignEditing) {
        toastObj.show({
          content: i18n
            .t('toasts.signDeletedSuccessfully')
            .replace('{}', deleteSelectedSign?.name),
          cssClass: 'e-toast-success',
          icon: 'lf-icon lf-icon-check-round'
        });
      }
      setDeleteSelectedSign(null);
    } catch (error) {
      console.error(error);
      toastObj.show({
        content: i18n
          .t('toasts.signDeleteError')
          .replace('{}', deleteSelectedSign?.name),
        cssClass: 'e-toast-danger',
        icon: 'lf-icon lf-icon-close-round'
      });
      setDeleteSelectedSign(null);
    }
  };

  const saveSign = async () => {
    try {
      if (currentSign === '' || editSignName === '') {
        toastObj.show({
          content: i18n.t('toasts.mandatoryFieldsError'),
          cssClass: 'e-toast-danger',
          icon: 'lf-icon lf-icon-close-round'
        });
        return;
      }

      if (getStringSizeInKB(currentSign) > 512) {
        setShowSizeErrorModal(true);
        return;
      }

      setIsLoading(true);
      const action =
        editingAccount && editingAccount.name === editSignName
          ? 'update'
          : 'create';
      await addOrUpdateSign({
        userId,
        provider: currentAccount.provider,
        email: currentAccount.email,
        name: editSignName,
        sign: currentSign,
        isDefault,
        action,
        lexonToken
      });
      if (editingAccount && editingAccount.name !== editSignName) {
        await handleRemoveSign(editingAccount, true);
      }
      getAccount();
      setIsLoading(false);
      setEditing(false);
      setCurrentSign('');
      setEditSignName('');
      setIsDefault(false);
      setEditingAccount(null);
      toastObj.show({
        content:
          action === 'create'
            ? i18n.t('toasts.signCreatedSuccessfully')
            : i18n.t('toasts.signUpdatedSuccessfully'),
        cssClass: 'e-toast-success',
        icon: 'lf-icon lf-icon-check-round'
      });
    } catch (error) {
      console.error(error);
      toastObj.show({
        content: i18n.t('toasts.signCreateError'),
        cssClass: 'e-toast-danger',
        icon: 'lf-icon lf-icon-close-round'
      });
    }
  };

  const handleCancel = () => {
    if (editing) {
      setEditing(false);
      setCurrentSign('');
      setEditSignName('');
      setEditingAccount(null);
      setIsDefault(false);
    } else {
      setShowUserSignPopover(false);
    }
  };

  return (
    <>
      <ToastComponent
        ref={toast => setToastObj(toast)}
        id="toast_pos"
        position={{ X: 'Right', Y: 'Top' }}
        animation={{ hide: { duration: '1' }, show: { duration: '200' } }}
        timeOut={7000}
        showCloseButton
      />
      <Confirmation
        initialModalState={deleteConfirmationOpen}
        onAccept={() => {
          handleRemoveSign(deleteSelectedSign);
          setDeleteConfirmationOpen(false);
        }}
        onCancel={() => {
          setDeleteSelectedSign(null);
          setDeleteConfirmationOpen(false);
        }}
        message={i18n
          .t('alerts.signDeleteConfirmation')
          .replace('{}', deleteSelectedSign?.name)}
        titleAccept={i18n.t('general.remove')}
        titleCancel={i18n.t('general.cancel')}
        confirmType="confirm-info"
        iconType="lf-icon lf-icon-information"
      />
      <Confirmation
        initialModalState={showSizeErrorModal}
        onAccept={() => setShowSizeErrorModal(false)}
        message={i18n.t('toasts.signSizeError')}
        titleAccept={i18n.t('general.accept')}
        confirmType="confirm-error"
        iconType="lf-icon lf-icon-close-round"
      />
      <Modal
        show={showUserSignPopover}
        onHide={() => {
          setEditing(false);
          setShowUserSignPopover(false);
        }}
        size="xl"
        aria-labelledby="contained-modal-title-vcenter"
        centered
        className="user-sign-modal"
      >
        <Modal.Header>
          <div className="user-sign-modal-header">
            <Modal.Title>{i18n.t('menu-user.email-signature')}</Modal.Title>
          </div>
        </Modal.Header>
        <Modal.Body>
          <div className="account-sign-body-container">
            {signs.length === 0 && !editing && (
              <div className="account-sign-empty-body">
                <div className="account-sign-empty-body-icon">
                  <TemplateLogo />
                </div>
                <div style={{ display: 'flex', flexDirection: 'column' }}>
                  <div className="body-text1">
                    {i18n.t('account-sign.professional_signature_text')}
                  </div>
                  <div className="body-text2">
                    <span onClick={() => addNewSign()}>
                      {i18n.t('account-sign.new_signature_text')}
                    </span>{' '}
                    {i18n.t('account-sign.use_when_you_need_text')}
                  </div>
                </div>
              </div>
            )}
            {editing && (
              <div className="mt-4">
                <div className="d-flex justify-content-end account-sign-editing-mandatory">
                  <span className="text-danger">*</span>
                  {i18n.t('general.required_field')}
                </div>
                <div className="account-sign-editing-title">
                  {i18n.t('account-sign.signature_name_text')}
                  <span className="text-danger">*</span>
                </div>
                <input
                  placeholder={i18n.t('account-sign.signature_name_text')}
                  type="text"
                  className="form-control account-sign-editing-title-input"
                  value={editSignName}
                  onChange={e => setEditSignName(e.target.value)}
                />

                <div className="d-flex my-3">
                  <div className="is-default-wrapper">
                    <i
                      className={`lf-icon icon-default ${
                        isDefault
                          ? 'lf-icon-switch-right-full'
                          : 'lf-icon-switch-left'
                      }`}
                      onClick={() => setIsDefault(!isDefault)}
                    />
                    <span className="default-text">
                      {i18n.t('account-sign.select_default_signature_text')}
                    </span>
                  </div>
                </div>
                <div className="account-sign-editing-title">
                  {i18n.t('account-sign.signature')}
                  <span className="text-danger">*</span>
                </div>
                <UserSign
                  onChange={setCurrentSign}
                  defaultValue={currentSign}
                />
              </div>
            )}
            {signs.length > 0 && !editing && (
              <>
                {signs.map(sign => (
                  <div
                    className="d-flex justify-content-between align-items-center mt-4 account-sign-list-item"
                    key={sign.name}
                  >
                    <div className="inter-medium-16 account-sign-list-sign">
                      <span>{sign.name}</span>
                      {sign.isDefault && (
                        <span className="account-sign-deafult-tag ml-2">
                          {i18n.t('account-sign.by_default')}
                        </span>
                      )}
                    </div>
                    <div className="d-flex">
                      <div className="mr-4">
                        <img
                          src="/assets/icons/firma/edit.svg"
                          alt="edit"
                          className="lf-icon lf-icon-edit"
                          onClick={() => {
                            setEditSignName(sign.name);
                            setCurrentSign(sign.sign);
                            setEditingAccount(sign);
                            setIsDefault(sign.isDefault);
                            setEditing(true);
                          }}
                        />
                      </div>
                      <div>
                        <img
                          src="/assets/icons/firma/delete.svg"
                          alt="delete"
                          className="lf-icon lf-icon-trash"
                          onClick={() => {
                            setDeleteSelectedSign(sign);
                            setDeleteConfirmationOpen(true);
                          }}
                          style={{ marginRight: '10px' }}
                        />
                      </div>
                    </div>
                  </div>
                ))}

                <div
                  className="account-add-sign mt-4"
                  onClick={() => addNewSign()}
                >
                  <img
                    src="/assets/icons/create.svg"
                    className="lf-icon lf-icon-add-round"
                  />
                  <span className="inter-medium-16 ml-2">
                    {i18n.t('account-sign.new_signature')}
                  </span>
                </div>
              </>
            )}
          </div>
        </Modal.Body>
        <Modal.Footer>
          <div className="d-flex justify-content-end modal-btn-container pt-4">
            <button
              type="button"
              className="mr-left font-weight-bold btn-outline-primary btn btn-secondary"
              onClick={handleCancel}
            >
              {editing ? i18n.t('general.cancel') : i18n.t('general.close')}
            </button>
            {!editing ? (
              <button
                type="button"
                className="mr-left font-weight-bold btn-primary btn"
                onClick={() => setEditing(true)}
              >
                {i18n.t('general.create')}
              </button>
            ) : (
              <button
                type="button"
                className="mr-left font-weight-bold btn-primary btn"
                onClick={saveSign}
              >
                {i18n.t('general.save')}
              </button>
            )}
          </div>
        </Modal.Footer>
      </Modal>
    </>
  );
};

const mapStateToProps = state => ({
  lexon: state.lexon
});

const mapDispatchToProps = dispatch =>
  bindActionCreators(
    {
      setFirmas
    },
    dispatch
  );

export default connect(mapStateToProps, mapDispatchToProps)(AccountSign);
